import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import {
  NgxGalleryImage,
  NgxGalleryImageSize,
  NgxGalleryOptions,
} from "@kolkov/ngx-gallery";
import { forkJoin, of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { ReaderService } from "../../services/reader.service";
import { DynamicHomepageService } from "../../services/dynamic-homepage.service";
import { SectionContent } from "../utility/SectionContent";
import { HomepageEditorComponent } from "./homepage-editor/homepage-editor.component";
import { MatDialog } from "@angular/material/dialog";
import { MediaType } from "../utility/UpdateMediaRequest";
import { LinkObject } from "../utility/LinkObject";
import { GetMediaForSectionResponse } from "../utility/GetMediaForSectionResponse";
import { AnnouncementEditorComponent } from "./announcement-editor/announcement-editor.component";
import { Annnouncement } from "../utility/utility-classes";

export class HomepageSection {
  sectionId: string;
  sectionName: string;
  sectionType: MediaType;
  sectionContent: SectionContent;
}

@Component({
    selector: "app-homepage",
    templateUrl: "./homepage.component.html",
    styleUrls: ["./homepage.component.scss"],
    standalone: false
})
export class HomepageComponent implements OnInit, OnDestroy {
  galleryOptions: NgxGalleryOptions[];
  galleryImages: NgxGalleryImage[] = [];
  images: any;
  @Input() editingHomepage = false;
  announcement: Annnouncement = new Annnouncement();
  private objectUrls: string[] = [];

  cpdContactsBool = false;
  cpdContacts: HomepageSection = {
    sectionId: "1",
    sectionName: "CPD Contacts",
    sectionType: MediaType.contact,
    sectionContent: {
      links: [],
      contacts: [],
      htmlTexts: [],
    },
  };

  supportLinksBool = false;
  supportLinks: HomepageSection = {
    sectionId: "2",
    sectionName: "Support Links",
    sectionType: MediaType.link,
    sectionContent: {
      links: [],
      contacts: [],
      htmlTexts: [],
    },
  };

  internalLinksBool = false;
  internalLinks: HomepageSection = {
    sectionId: "3",
    sectionName: "Internal Links",
    sectionType: MediaType.link,
    sectionContent: {
      links: [],
      contacts: [],
      htmlTexts: [],
    },
  };

  vehicleEntryCountsBool = false;
  vehicleEntryCounts: HomepageSection = {
    sectionId: "4",
    sectionName: "CPD POWER BI DASHBOARD",
    sectionType: MediaType.htmlText,
    sectionContent: {
      links: [],
      contacts: [],
      htmlTexts: [],
    },
  };

  constructor(
    private readerService: ReaderService,
    private homepageService: DynamicHomepageService,
    private dialog: MatDialog
  ) {}

  ngOnInit() {
    this.readerService.getHomeImages().subscribe({
      next: (response) => {
        this.images = response || [];
        const requests = this.images
          .filter((image) => image !== undefined && image !== null)
          .map((image) =>
            this.readerService.getAuthenticatedImageUrl(image).pipe(
              map((url) => {
                this.objectUrls.push(url);
                return { medium: url, big: url };
              }),
              catchError(() => of(null))
            )
          );

        if (requests.length === 0) {
          this.galleryImages = [];
          this.update();
          return;
        }

        forkJoin(requests).subscribe({
          next: (imagesURL: Array<NgxGalleryImage | null>) => {
            this.galleryImages = imagesURL.filter(
              (image): image is NgxGalleryImage => image != null
            );
            this.update();
          },
          error: () => {
            this.galleryImages = [];
            this.update();
          },
        });
      },
      error: () => {
        this.galleryImages = [];
        this.update();
      },
    });

    this.readerService.getAnnouncement().subscribe((response) => {
      this.announcement = response;
    });

    this.galleryOptions = [
      {
        thumbnails: false,
        imageAutoPlay: true,
        imageAutoPlayPauseOnHover: true,
        imageSize: NgxGalleryImageSize.Contain,
        width: "100%",
        height: "100%",
        imageArrows: false,
        imageAutoPlayInterval: 5000,
      },
    ];
  }

  ngOnDestroy(): void {
    this.objectUrls.forEach((url) => {
      if (typeof URL !== "undefined" && typeof URL.revokeObjectURL === "function") {
        URL.revokeObjectURL(url);
      }
    });
  }

  openHomepageEditor(sectionName) {
    let section: HomepageSection;
    switch (sectionName) {
      case "cpdContact":
        section = this.cpdContacts;
        break;
      case "supportLinks":
        section = this.supportLinks;
        break;
      case "internalLinks":
        section = this.internalLinks;
        break;
      case "vehicleEntryCounts":
        section = this.vehicleEntryCounts;
        break;
      default:
        section = null;
        break;
    }
    const dialogRef = this.dialog.open(HomepageEditorComponent, {
      width: "85%",
      data: {
        section,
      },
    });

    dialogRef.afterClosed().subscribe(() => {
      this.update();
    });
  }

  reloadAnnouncements() {
    //Grab updated announcement
    this.readerService.getAnnouncement().subscribe((response) => {
      this.announcement = response;
    });
  }

  /**
   * Updates the text and links available to users on the homepage.
   */
  update() {
    this.homepageService.editingHomepage.subscribe((data) => {
      this.editingHomepage = data;
    });

    if (!this.cpdContactsBool) {
      this.cpdContactsBool = true;
      this.homepageService
        .getMediaForSection(this.cpdContacts.sectionId)
        .subscribe({
          next: (res: GetMediaForSectionResponse) => {
            this.cpdContacts.sectionContent.contacts = res.contacts;
            this.cpdContactsBool = false;
          },
          error: (_) => {
            this.cpdContactsBool = false;
          },
        });
    }

    if (!this.supportLinksBool) {
      this.supportLinksBool = true;
      this.homepageService
        .getMediaForSection(this.supportLinks.sectionId)
        .subscribe({
          next: (res: GetMediaForSectionResponse) => {
            this.supportLinks.sectionContent.links = res.links;
            this.supportLinksBool = false;
          },
          error: (_) => {
            this.supportLinksBool = false;
          },
        });
    }

    if (!this.internalLinksBool) {
      this.internalLinksBool = true;
      this.homepageService
        .getMediaForSection(this.internalLinks.sectionId)
        .subscribe({
          next: (res: GetMediaForSectionResponse) => {
            this.internalLinks.sectionContent.links = res.links;
            this.internalLinksBool = false;
          },
          error: (_) => {
            this.internalLinksBool = false;
          },
        });
    }

    if (this.vehicleEntryCountsBool) {
      this.vehicleEntryCountsBool = true;
      this.homepageService
        .getMediaForSection(this.vehicleEntryCounts.sectionId)
        .subscribe({
          next: (res: GetMediaForSectionResponse) => {
            this.vehicleEntryCounts.sectionContent.htmlTexts = res.htmlTexts;
            this.vehicleEntryCountsBool = false;
          },
          error: (_) => {
            this.vehicleEntryCountsBool = false;
          },
        });
    }
  }

  finishEditing() {
    this.homepageService.disableHomepageEditor();

    this.homepageService.editingHomepage.subscribe((data) => {
      this.editingHomepage = data;
    });
  }

  openAnnouncementEditor() {
    const dialogRef = this.dialog.open(AnnouncementEditorComponent, {
      data: {
        message: this.announcement.message,
        is_activated: this.announcement.is_activated,
      },
    });

    dialogRef.afterClosed().subscribe(() => {
      this.reloadAnnouncements();
    });
  }

  hasDescription(link: LinkObject): boolean {
    if (link.description) {
      return link.description.length > 0;
    }
    return false;
  }
}
